import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { globalProductIdManagerToken } from "@/tokens";
import { QuestionsListWidgetDefinition } from "@apps/reviews/definitions";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const REVIEW_PRODUCT_ID = "gw-product-id";
const ADD_QUESTION_BUTTON_ENABLED = "gw-add-question-button-enabled";

class QuestionsListWidgetInjector {
    private ioi: IntersectionObserverInjector;

    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            this.placeholderSelector,
            {
                rootMargin: "0px 0px 50% 0px",
            }
        );
    }

    start(): void {
        this.logger.debug("QuestionsListWidgetInjector start", {
            placeholderSelector: this.placeholderSelector,
        });

        this.ioi.targets$.subscribe((placeholder) => {
            this.logger?.debug(
                "QuestionsListWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            const productIdFromDataAttr =
                parseIntDataAttr(placeholder, REVIEW_PRODUCT_ID) ||
                parseIntFromGidDataAttr(placeholder, REVIEW_PRODUCT_ID);

            if (productIdFromDataAttr === null) {
                this.logger.error(
                    `QuestionsListWidget placeholder does not have data-${REVIEW_PRODUCT_ID} attribute.`
                );
            }

            const addQuestionButtonEnabledDataAttr =
                parseOptionalBooleanDataAttr(
                    placeholder,
                    ADD_QUESTION_BUTTON_ENABLED
                );

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
            }

            void (async () => {
                const QuestionsListWidget =
                    await this.dynamicImportManager.importDefault(
                        QuestionsListWidgetDefinition,
                        true
                    );

                const widget = new QuestionsListWidget(
                    {
                        target: placeholder,
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                        productIdManager,
                        showAddQuestionButton:
                            addQuestionButtonEnabledDataAttr ?? true,
                    },
                    this.logger
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(
                        REVIEWS_WIDGET_NAMES.QUESTIONS_LIST_WIDGET
                    )
                );

                dispatchSdkEvent(
                    new WidgetInjectedSdkEvent(
                        REVIEWS_WIDGET_NAMES.QUESTIONS_LIST_WIDGET
                    )
                );
            })();
        });
        this.ioi.start();
    }
}

export default QuestionsListWidgetInjector;
